/**
 * 商家端 - 资金账户
 *
 * 展示：
 *   - 主体信息（含虚拟子户号 bank_sub_account）
 *   - 本月服务费收入（汇总 SETTLED/INVOICED 订单 amount_total × commission_rate）
 *   - 订单维度的服务费收入列表
 *
 * 注：后端没有"我的分账"列表端点，故从订单维度汇总。
 * 佣金率来自合作关系（订单上有 partnership_id）。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Table, Badge, useApi, money, time, Loading, Button } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Merchant {
  merchant_id: string
  company_name: string
  bank_sub_account: string | null
  status: string
  tax_no: string
}

interface Order {
  order_id: string
  partnership_id: string
  amount_total: string
  status: string
  synced_at: string
  logistics_no: string | null
}

interface Partnership {
  partnership_id: string
  commission_rate: string
}

/** 计入服务费收入的订单状态：PAID（已支付即已分账）；REFUNDED 不计入 */
const REVENUE_STATES = new Set(['PAID'])

function isSameMonth(iso: string): boolean {
  if (!iso) return false
  const d = new Date(iso)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
}

export function SellerFinance() {
  const nav = useNavigate()
  const me = useApi<Merchant>(async () => api.get('/merchants/me'), [])
  const orders = useApi<Order[]>(async () => api.get('/orders'), [])
  const partnerships = useApi<Partnership[]>(async () => api.get('/partnerships'), [])

  // 构建 partnership_id -> commission_rate 映射
  const rateMap = new Map<string, number>()
  for (const p of partnerships.data ?? []) {
    rateMap.set(p.partnership_id, Number(p.commission_rate))
  }

  const revenueOrders = (orders.data ?? []).filter((o) => REVENUE_STATES.has(o.status))

  const totalRevenue = revenueOrders.reduce((sum, o) => {
    const rate = rateMap.get(o.partnership_id) ?? 0
    return sum + Number(o.amount_total) * rate
  }, 0)

  const monthRevenue = revenueOrders
    .filter((o) => isSameMonth(o.synced_at))
    .reduce((sum, o) => {
      const rate = rateMap.get(o.partnership_id) ?? 0
      return sum + Number(o.amount_total) * rate
    }, 0)

  if (me.loading) return <Loading />
  if (me.error) return <Loading error={me.error} />

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>资金账户</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
        <MetricCard label="虚拟子户号" value={me.data?.bank_sub_account ?? '未开通'} />
        <MetricCard label="本月服务费收入" value={money(monthRevenue)} />
        <MetricCard label="累计服务费收入" value={money(totalRevenue)} />
      </div>

      {/* 提现（INT-B bank.withdraw）*/}
      <WithdrawCard merchantId={me.data?.merchant_id} />

      <Card title="主体信息">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, fontSize: 13 }}>
          <Field label="主体ID" value={me.data?.merchant_id ?? '-'} />
          <Field label="主体名称" value={me.data?.company_name ?? '-'} />
          <Field label="税号" value={me.data?.tax_no ?? '-'} />
          <Field label="虚拟子户号" value={me.data?.bank_sub_account ?? '未开通'} />
          <Field label="主体状态" value={me.data?.status ?? '-'} />
        </div>
      </Card>

      <Card title={`订单服务费明细（${revenueOrders.length} 笔已分账）`}>
        {orders.loading ? (
          <Loading />
        ) : (
          <Table<Order>
            rows={orders.data ?? []}
            empty="暂无订单"
            columns={[
              { key: 'order_id', title: '订单号', render: (r) => <a onClick={() => nav(`/seller/orders?detail=${r.order_id}`)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
              { key: 'synced_at', title: '同步时间', render: (r) => time(r.synced_at) },
              { key: 'amount_total', title: '订单金额', render: (r) => money(r.amount_total) },
              {
                key: 'rate',
                title: '佣金率',
                render: (r) => {
                  const rate = rateMap.get(r.partnership_id)
                  return rate != null ? `${(rate * 100).toFixed(1)}%` : '-'
                },
              },
              {
                key: 'fee',
                title: '服务费',
                render: (r) => {
                  const rate = rateMap.get(r.partnership_id) ?? 0
                  return money(Number(r.amount_total) * rate)
                },
              },
              { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            ]}
          />
        )}
      </Card>

      <HelpBubble pageKey="seller-finance" title="资金账户">
        <p>资金流：消费者在电商付款 → <b>商家充值</b>到平台 → 平台用这笔钱分账（服务费留商家、货款给工厂、抽佣给平台）。</p>
        <p>点订单号可跳转订单详情。余额不足时无法分账，需先充值。</p>
      </HelpBubble>
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}

/** 提现卡片：查余额 + 提现按钮（INT-B bank.withdraw）。商家/工厂通用 */
export function WithdrawCard({ merchantId }: { merchantId?: string }) {
  const balance = useApi<Array<{ sub_account_id: string; type: string; balance: string }>>(
    async () => api.get('/merchants/me/balance'),
    [merchantId],
  )
  const [mode, setMode] = useState<'none' | 'recharge' | 'withdraw'>('none')
  const [amount, setAmount] = useState('')
  const [target, setTarget] = useState('622848...')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [okMsg, setOkMsg] = useState('')

  const myAcct = (balance.data ?? []).find((a) => a.type === 'SELLER' || a.type === 'FACTORY')
  const myBalance = myAcct ? parseFloat(myAcct.balance) : 0

  async function recharge() {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) return setErr('金额必须 > 0')
    setBusy(true); setErr(''); setOkMsg('')
    try {
      await api.post('/settlements/recharge', { amount: amt })
      setOkMsg(`✓ 充值成功 ¥${amt}`)
      setAmount('')
      balance.refresh()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  async function withdraw() {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) return setErr('金额必须 > 0')
    setBusy(true); setErr(''); setOkMsg('')
    try {
      const r = await api.post<{ serial_no: string; amount: number }>('/settlements/withdraw', {
        amount: amt,
        targetBankAccount: target,
      })
      setOkMsg(`✓ 提现成功，流水号 ${r.serial_no}`)
      setAmount('')
      balance.refresh()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="💳 资金账户（INT-B 银行）"
      action={
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size="sm" onClick={() => setMode((m) => (m === 'recharge' ? 'none' : 'recharge'))}>充值</Button>
          {myBalance > 0 && (
            <Button size="sm" variant="default" onClick={() => setMode((m) => (m === 'withdraw' ? 'none' : 'withdraw'))}>提现</Button>
          )}
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
        <div>
          <div style={{ color: '#888', fontSize: 12 }}>可用余额</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: myBalance > 0 ? '#166534' : '#666' }}>{money(myBalance)}</div>
        </div>
        {myAcct && <div style={{ fontSize: 12, color: '#888' }}>子户：{myAcct.sub_account_id}</div>}
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
        商家把电商结算的钱充值到平台，平台用这笔钱分账（货款→工厂、服务费留存、抽佣→平台）。
      </div>

      {mode === 'recharge' && (
        <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', borderRadius: 4 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="number"
              placeholder="充值金额"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, width: 120 }}
            />
            <Button size="sm" onClick={recharge} disabled={busy}>
              {busy ? '处理中...' : '确认充值'}
            </Button>
          </div>
          {err && <div style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{err}</div>}
          {okMsg && <div style={{ color: '#16a34a', marginTop: 6, fontSize: 12 }}>{okMsg}</div>}
        </div>
      )}

      {mode === 'withdraw' && (
        <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 4 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="number"
              placeholder="提现金额"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, width: 120 }}
            />
            <input
              placeholder="目标对公账户"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, flex: 1, minWidth: 200 }}
            />
            <Button size="sm" onClick={withdraw} disabled={busy}>
              {busy ? '处理中...' : '确认提现'}
            </Button>
          </div>
          {err && <div style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{err}</div>}
          {okMsg && <div style={{ color: '#16a34a', marginTop: 6, fontSize: 12 }}>{okMsg}</div>}
        </div>
      )}
    </Card>
  )
}
